/*
* All site text, links & photo lists, taken word for word from the Wix site (www.graylagco.co.za)
* Photos are listed by id - the file name in public/images/photos (full size, up to 2400px) and public/images/photos/md (1200px)
*/
import { PHOTO_SIZES } from "./photos";

export const SITE_NAME = "Graylag & Co";
export const SITE_URL = "https://www.graylagco.co.za";
export const DEFAULT_DESCRIPTION = "Step into a realm of refined living with Graylag & Co, where custom cabinetry transcends the ordinary. Our creations merge functionality and aesthetics seamlessly, a testament to our commitment to complementing your unique style. Visit our showroom to immerse yourself in the timeless precision of designs tailored for kitchens, bedrooms, walk-in closets, bathrooms, offices, and entertainment spaces. Elevate your home with our exquisite craftsmanship.";

//Page URLs are the same as on Wix, so existing links & search results keep working
export const ROUTES = {
  HOME: "/",
  ABOUT: "/about",
  KITCHENS: "/kitchen",
  BEDROOMS: "/bedrooms",
  BATHROOMS: "/bathrooms",
  ENTERTAINMENT: "/entertainment",
  GALLERY: "/gallery",
  SHOWROOM: "/showroom",
  CONTACT: "/contact",
};

//"Rooms" opens a drop-down with the four room pages
export const NAV_LINKS = [
  { label: "Home", path: ROUTES.HOME },
  { label: "About", path: ROUTES.ABOUT },
  {
    label: "Rooms",
    children: [
      { label: "Kitchens", path: ROUTES.KITCHENS },
      { label: "Bedrooms", path: ROUTES.BEDROOMS },
      { label: "Bathrooms", path: ROUTES.BATHROOMS },
      { label: "Entertainments Spaces", path: ROUTES.ENTERTAINMENT },
    ],
  },
  { label: "Gallery", path: ROUTES.GALLERY },
  { label: "Showroom", path: ROUTES.SHOWROOM },
  { label: "Contact", path: ROUTES.CONTACT },
];

export const CONTACT_DETAILS = {
  PHONE: { label: "+27 81 803 1596", link: "tel:+27818031596" },
  WHATSAPP_LINK: "https://wa.me/27818031596",
  FACEBOOK_LINK: "https://www.facebook.com/profile.php?id=61551364331263",
  INSTAGRAM_LINK: "https://www.instagram.com/graylagco/",
  ADDRESS: [
    "First floor, Riverside Shopping Center,",
    "319 Bryanston Dr, Bryanston,",
    "Johannesburg, 2191",
  ],
  MAP_LINK: "https://www.google.com/maps/search/?api=1&query=Graylag+%26+Co+Riverside+Shopping+Centre+319+Bryanston+Drive+Bryanston",
  CREDIT: { label: "TapX", link: "https://tap-x.com/" },
};

//Turns photo ids into what the galleries need: full size & 1200px versions (srcSet lets the browser pick the
//sharpest one it needs for the screen), size and alt text
export const photos = (ids, alt) => ids.map((id, i) => {
  const [width, height] = PHOTO_SIZES[id] || [2400, 1600];
  const src = `/images/photos/${id}.webp`;
  const md = `/images/photos/md/${id}.webp`;
  return {
    id,
    src,
    md,
    srcSet: width > 1200 ? `${md} 1200w, ${src} ${width}w` : `${src} ${width}w`,
    width,
    height,
    alt: ids.length > 1 ? `${alt} ${i + 1}` : alt,
  };
});

//Slideshow at the top of Home, About, Showroom & Contact
export const HERO_SLIDES = photos(["2aad0a4e", "29934bed", "68bdccd2", "7099fe0b", "20573c45", "874ea7fa"], "Graylag & Co bespoke cabinetry");

export const HOME = {
  INTRO: {
    TITLE: "EXQUISITE BESPOKE CABINETRY",
    TEXT: "Step into a realm of refined living with Graylag & Co, where bespoke cabinetry transcends the ordinary. Our creations merge functionality and aesthetics seamlessly, a testament to our commitment to complementing your unique style. Visit our showroom to immerse yourself in the timeless precision of designs tailored for kitchens, bedrooms, walk-in closets, bathrooms, offices, and entertainment spaces. Elevate your home with our exquisite craftsmanship.",
  },
  //The four tabs under the intro
  ROOMS: [
    {
      TAB: "Kitchens",
      TITLE: "KITCHENS",
      TEXT: "Unleash culinary artistry with Graylag & Co's bespoke kitchens. Every kitchen is designed and built specifically for that client, so no two are ever the same- seamlessly blending functionality and style. From innovative storage solutions to exquisite finishes, our kitchens redefine the heart of your home.",
      BUTTON: "Graylag & Co Kitchens",
      LINK: ROUTES.KITCHENS,
      PHOTOS: photos(["f962c705", "7817b43f", "5e8fd69e", "4dd358c5", "77dbb717", "d9c40a5f", "e7546373", "b7ce267c", "09387337", "2aad0a4e", "b691205c", "baff00a9", "9b151399", "aaa6106c", "250d8eb5"], "Graylag & Co kitchen"),
    },
    {
      TAB: "Bedrooms",
      TITLE: "BEDROOMS",
      TEXT: "Indulge in the epitome of comfort and style with Graylag & Co's bespoke bedroom solutions. Crafted for serene spaces that reflect your unique taste, our designs seamlessly merge impeccable aesthetics with functionality. Transform your bedroom into a sanctuary of relaxation and sophistication.",
      BUTTON: "Graylag & Co Bedrooms",
      LINK: ROUTES.BEDROOMS,
      PHOTOS: photos(["a176e8b8", "f35628cf", "8d3d22a1", "08261505", "fb794780", "92cd544e", "7a420e20", "4e6b14e7", "f28ab1ae", "1d44dcd8", "1b4dbef3", "c26930d4", "4e447e8a", "5f209e8d"], "Graylag & Co bedroom"),
    },
    {
      TAB: "Bathrooms",
      TITLE: "BATHROOMS",
      TEXT: "Elevate your daily rituals with Graylag & Co's bespoke bathroom designs. Immerse yourself in spaces seamlessly marrying practicality and aesthetics. From custom vanities to innovative storage, our designs redefine the concept of luxurious bathing.",
      BUTTON: "Graylag & Co Bathrooms",
      LINK: ROUTES.BATHROOMS,
      PHOTOS: photos(["2a574ede", "4d9bece3", "ad8610e6", "e886942f", "ffc059a2", "d0b34e9e", "70386349"], "Graylag & Co bathroom"),
    },
    {
      TAB: "Entertainment Spaces",
      TITLE: "ENTERTAINMENT SPACES",
      TEXT: "Redesign leisure with Graylag & Co's bespoke entertainment spaces. From custom media cabinets to state of the art bars and outdoor areas, our designs redefine how you unwind and host. Experience the perfect blend of functionality and style, creating spaces where entertainment becomes an art form.",
      BUTTON: "Graylag & Co Entertainment Spaces",
      LINK: ROUTES.ENTERTAINMENT,
      PHOTOS: photos(["29632188"], "Graylag & Co entertainment space"),
    },
  ],
  SHOWROOM: {
    TITLE: "Visit Our Showroom",
    TEXT: "Unleash culinary artistry with Graylag & Co's bespoke kitchens. Every kitchen is designed and built specifically for that client, so no two are ever the same- seamlessly blending functionality and style. From innovative storage solutions to exquisite finishes, our kitchens redefine the heart of your home.",
    LEFT_PHOTO: photos(["a12e0fdc"], "Graylag & Co showroom kitchen island")[0],
    RIGHT_PHOTO: photos(["6059fc58"], "Graylag & Co showroom cabinetry")[0],
  },
};

//"We look forward to ..." band above the footer on Home & Contact
export const CONSULTATION = {
  TEXT: "We look forward to exceeding your expectations and addressing your needs. To schedule a consultation, please chat with us on WhatsApp.",
  BUTTON: "Book Your Consultation on WhatsApp",
};

export const ABOUT = {
  TITLE: "WHY GRAYLAG & CO",
  SECTIONS: [
    {
      TITLE: "Our Design Process",
      TEXT: "With over 35 years of experience, we understand the importance of having a luxury bespoke kitchen or space designed for your individual needs and aspirations. Our team will take you on a design journey that commences with a personalized consultation, where we find out more about your preferences and requirements. Utilizing state-of-the-art software, we craft 3D renderings that provide a glimpse into the future—allowing you to visualize the finished product. Our designers, with a wealth of experience, are dedicated to tailoring every detail to your unique tastes.",
      PHOTO: photos(["839a6f54"], "Bespoke dining room cabinetry and wine storage")[0],
    },
    {
      TITLE: "CRAFTING QUALITY",
      TEXT: "At Graylag & Co, quality is the cornerstone of our commitment. Employing the latest Computer Numerical Control (CNC) technology, our bespoke cabinets are meticulously handmade to perfection.",
      PHOTO: photos(["71995bc7"], "Cabinet maker measuring timber")[0],
    },
    {
      TITLE: "Tailored Solutions",
      TEXT: "Experience the difference of tailored solutions with our bespoke cabinets. Our expert cabinet builders prioritize the uniqueness of your space and style, working closely with you to create the perfect cabinet for your home. We pride ourselves on our attention to detail, and use only the highest quality materials to deliver top-notch results every time.",
      PHOTO: photos(["7df2de05"], "Walk-in wine and pantry cabinetry")[0],
    },
  ],
};

//Kitchens, Bedrooms, Bathrooms & Entertainment Spaces pages: banner, intro and style cards (each a small slideshow)
export const ROOM_PAGES = {
  KITCHENS: {
    META_TITLE: "Kitchens",
    WIDE_TEXT: true, //The intro text runs a little wider on this page on Wix
    HERO: { VIDEO: "/images/kitchens-hero.mp4", POSTER: "/images/kitchens-hero-poster.webp" },
    TITLE: "KITCHENS",
    TEXT: "Unleash culinary artistry with Graylag & Co's bespoke kitchens. Seamlessly blending functionality and style, our designs inspire gastronomic creativity. From innovative storage solutions to exquisite finishes, our kitchens redefine the heart of your home. Contact one of our consultants today to transform your kitchen into a culinary masterpiece.",
    CARDS: [
      { TITLE: "Traditional Elegance", TEXT: "Classic designs that exude timeless charm.", PHOTOS: photos(["7b8e0883", "10581abc", "5820bfd3"], "Traditional kitchen") },
      { TITLE: "Contemporary Sophistication", TEXT: "Sleek and stylish solutions for the modern home.", PHOTOS: photos(["387ee596", "e1f84461", "f88f7557"], "Contemporary kitchen") },
      { TITLE: "Modern Innovation", TEXT: "Cutting-edge designs that redefine luxury living.", PHOTOS: photos(["b47e7ae2", "77dbb717", "91a737aa"], "Modern kitchen") },
    ],
  },
  BEDROOMS: {
    META_TITLE: "Bedrooms",
    HERO: { PHOTO: photos(["0b792b77"], "Graylag & Co bedroom cupboards")[0] },
    TITLE: "BEDROOMS",
    TEXT: "Indulge in the epitome of comfort and style with Graylag & Co's bespoke bedroom solutions. Crafted for serene spaces that reflect your unique taste, our designs seamlessly merge impeccable aesthetics with functionality. Transform your bedroom into a sanctuary of relaxation and sophistication. Contact us to embark on the journey of elevating your personal haven.",
    CARDS: [
      { TITLE: "Traditional Bedrooms", TEXT: "Warm, elegant, and full of character, with classic detailing and rich materials.", PHOTOS: photos(["1955f86a", "27d7c0a5", "4e447e8a"], "Traditional bedroom") },
      { TITLE: "Contemporary Bedrooms", TEXT: "A perfect blend of current trends and timeless comfort, featuring bold contrasts and natural textures.", PHOTOS: photos(["f35628cf", "a176e8b8", "f28ab1ae"], "Contemporary bedroom") },
      { TITLE: "Modern Bedrooms", TEXT: "Sleek, minimal, and functional spaces with clean lines and a calming aesthetic.", PHOTOS: photos(["60b536a6", "e4fdc89d"], "Modern bedroom") },
    ],
  },
  BATHROOMS: {
    META_TITLE: "Bathrooms",
    HERO: { PHOTO: photos(["6795a9c5"], "Graylag & Co bathroom vanity")[0] },
    TITLE: "BATHROOMS",
    TEXT: "Elevate your daily rituals with Graylag & Co's bespoke bathroom designs. Immerse yourself in spaces seamlessly marrying practicality and aesthetics. From custom vanities to innovative storage, our designs redefine the concept of luxurious bathing. Contact us to infuse your bathroom with bespoke elegance.",
    CARDS: [
      { TITLE: "Traditional Bathrooms", TEXT: "Timeless elegance with intricate detailing, soft tones, and refined fixtures.", PHOTOS: photos(["7302fe89", "d0b34e9e", "4b0c69d7", "9b16bc06"], "Traditional bathroom") },
      { TITLE: "Contemporary Bathrooms", TEXT: "A stylish mix of textures and shapes, combining modern convenience with artistic flair.", PHOTOS: photos(["ffc059a2", "2a574ede", "70386349"], "Contemporary bathroom") },
      { TITLE: "Modern Bathrooms", TEXT: "Chic and streamlined, with a focus on simplicity, neutral tones, and luxurious finishes.", PHOTOS: photos(["17a1d502", "ed88c11b"], "Modern bathroom") },
    ],
  },
  ENTERTAINMENT: {
    META_TITLE: "Entertainment Spaces",
    HERO: { PHOTO: photos(["6e48da6d"], "Graylag & Co TV unit and shelving")[0] },
    TITLE: "Entertainment Spaces",
    TEXT: "Redesign leisure with Graylag & Co's bespoke entertainment spaces. From custom media cabinets to statement pieces, our designs redefine how you unwind. Experience the perfect blend of functionality and style, creating spaces where entertainment becomes an art form. Contact us to embark on the journey of transforming your leisure spaces into showcases of bespoke luxury.",
    CARDS: [
      { TITLE: "Bar", TEXT: "Crafted for Conversation", PHOTOS: photos(["a5420c89", "5cc94bf8", "be3149f3"], "Bar") },
      { TITLE: "Patio", TEXT: "Seamless Outdoor Luxury", PHOTOS: photos(["0c826148", "41a1359f", "29632188"], "Patio") },
      { TITLE: "TV & Media Units", TEXT: "Designed for the Spotlight", PHOTOS: photos(["674870c2", "acc2768a", "fce62083", "ecec0902"], "TV and media unit") },
      { TITLE: "Library", TEXT: "Timeless Design", PHOTOS: photos(["258732a4", "ee744aeb"], "Library") },
    ],
  },
};

export const GALLERY = [
  {
    ID: "kitchens",
    TITLE: "KITCHEN GALLERY",
    TABS: [
      { LABEL: "Traditional Kitchens", PHOTOS: photos(["874ea7fa", "7b8e0883", "4f113ca7"], "Traditional kitchen") },
      { LABEL: "Contemporary Kitchens", PHOTOS: photos(["292a1026", "e8ea573d"], "Contemporary kitchen") },
      { LABEL: "Modern Kitchen", PHOTOS: photos(["8818c43b", "8bd7c181"], "Modern kitchen") },
    ],
  },
  {
    ID: "bedrooms",
    TITLE: "BEDROOM GALLERY",
    TABS: [
      { LABEL: "Traditional Bedroom", PHOTOS: photos(["1955f86a", "27d7c0a5", "c26930d4", "6de26e76", "4e447e8a", "5f209e8d", "1b4dbef3"], "Traditional bedroom") },
      { LABEL: "Contemporary Bedroom", PHOTOS: photos(["92cd544e", "f35628cf", "7a420e20", "4e6b14e7", "a176e8b8", "f28ab1ae"], "Contemporary bedroom") },
      { LABEL: "Modern Bedroom", PHOTOS: photos(["e4fdc89d", "60b536a6"], "Modern bedroom") },
    ],
  },
  {
    ID: "bathrooms",
    TITLE: "BATHROOM GALLERY",
    TABS: [
      { LABEL: "Traditional Bathroom", PHOTOS: photos(["7302fe89", "d0b34e9e", "4b0c69d7", "9b16bc06"], "Traditional bathroom") },
      { LABEL: "Contemporary Bathrooms", PHOTOS: photos(["ffc059a2", "ad8610e6", "2a574ede", "70386349"], "Contemporary bathroom") },
      { LABEL: "Modern Bathrooms", PHOTOS: photos(["17a1d502", "ed88c11b"], "Modern bathroom") },
    ],
  },
  {
    ID: "entertainment",
    TITLE: "ENTERTAINMENT SPACES GALLERY",
    TABS: [
      { LABEL: "Bar", PHOTOS: photos(["cd4a9f55", "ce9cdb8c", "9f029f8e", "3eb1a168", "be3149f3", "ea8b9d00", "5cc94bf8", "a5420c89", "ce96e121", "65ca762a", "0186ccc4", "074431b7"], "Bar") },
      { LABEL: "TV & Media Unit", PHOTOS: photos(["674870c2", "3ed1ac20", "23df728f", "acc2768a", "c204bf34", "fce62083", "ecec0902"], "TV and media unit") },
      { LABEL: "Patio", PHOTOS: photos(["e33edbda", "f185deec", "29632188", "41a1359f", "7336f6af", "56804c04", "0c826148", "dacaf7ed"], "Patio") },
      { LABEL: "Library", PHOTOS: photos(["258732a4", "ee744aeb"], "Library") },
    ],
  },
];

export const SHOWROOM = {
  TITLE: "SHOWROOM",
  TEXT: "Step into the immersive world of Graylag & Co by visiting our exquisite showroom—a living testament to the quality and craftsmanship that defines our bespoke cabinetry. At our showroom, you’ll be able to see – and feel – the superb quality and craftsmanship of our products and explore a diverse array of styles and finishes, each showcasing the artistry and versatility embedded in our different ranges. Our showroom is a curated space where inspiration comes to life. Let us walk you through the textures, colours, and features that make Graylag & Co a beacon of bespoke excellence.",
};

export const CONTACT = {
  TITLE: "Contact Us",
  AREAS: ["Kitchen", "Bathroom", "Vanity", "Patio", "TV Unit", "Wardrobe", "Other"],
  HOURS_TITLE: "Hours",
  HOURS: [
    "Monday to Friday: 09:00-17:00",
    "Saturdays: 09:00-13:00",
    "Sundays & Public Holidays- By Appointment Only",
  ],
};
